import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DateField } from "@/components/dateFields";
import { AlertCircle, FileText, Eye, Pencil } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogDescription,
} from "../../../components/ui/Dialog";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import {
  createDraft,
  updateDraft,
  coerceKind,
  type PlatformTermsKind,
  type PlatformTermsView,
} from "../../../api/General/PlatformTerms";
import { Error as ApiError } from "../../../models/general";
import SafeMarkdown from "../../../components/terms/SafeMarkdown";
import { apiDateOnlyParam } from '@/utils/apiDate';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When seed.id === 0, treat as a "new draft" seeded from the latest published version. */
  seed: PlatformTermsView | null;
  defaultKind: PlatformTermsKind;
  onSaved: (saved?: PlatformTermsView) => void;
}

const TermsDraftDialog: React.FC<Props> = ({ open, onOpenChange, seed, defaultKind, onSaved }) => {
  const { t } = useTranslation();

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState<string>("");
  const [view, setView] = useState<"edit" | "split" | "preview">("split");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const kind: PlatformTermsKind = useMemo(
    () => coerceKind(seed?.kind ?? defaultKind),
    [seed?.kind, defaultKind],
  );

  const isEditing = !!seed && seed.id > 0;

  useEffect(() => {
    if (!open) return;
    setTitle(seed?.title || "");
    setContent(seed?.contentMarkdown || "");
    setEffectiveFrom(
      apiDateOnlyParam(seed?.effectiveFrom),
    );
    setError(null);
    setSubmitting(false);
    setView("split");
  }, [open, seed]);

  const submit = async () => {
    if (!title.trim()) {
      setError(t("Title is required."));
      return;
    }
    if (!content.trim()) {
      setError(t("Content is required."));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        kind,
        title: title.trim(),
        contentMarkdown: content,
        effectiveFrom: effectiveFrom ? new Date(effectiveFrom).toISOString() : null,
      };
      const res = isEditing
        ? await updateDraft(seed!.id, payload)
        : await createDraft(payload);
      if (res.errors && res.errors.length > 0) {
        setError(res.errors[0]?.message || res.errors[0]?.description || t("Failed to save draft"));
        setSubmitting(false);
        return;
      }
      onSaved(res.payload || undefined);
    } catch (e: unknown) {
      const apiErr =
        (e as { errors?: ApiError[] })?.errors?.[0] ||
        (e as { response?: { data?: { errors?: ApiError[] } } })?.response?.data?.errors?.[0];
      setError(apiErr?.description || apiErr?.message || t("Failed to save draft"));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl" className="flex flex-col max-h-[92vh]">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <FileText size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <DialogTitle>
                {isEditing ? t("Edit draft") : t("New draft")}
              </DialogTitle>
              <DialogDescription className="mt-1">
                {t("Drafts are private until you publish. Publishing forces all users to re-accept on next login.")}
              </DialogDescription>
            </div>
            {/* View toggle */}
            <div className="inline-flex items-center rounded-md border border-border bg-muted/40 p-0.5">
              <ViewBtn active={view === "edit"} onClick={() => setView("edit")} label={t("Edit")} icon={<Pencil size={12} />} />
              <ViewBtn active={view === "split"} onClick={() => setView("split")} label={t("Split")} icon={<><Pencil size={12} className="me-0.5" /><Eye size={12} /></>} />
              <ViewBtn active={view === "preview"} onClick={() => setView("preview")} label={t("Preview")} icon={<Eye size={12} />} />
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="flex-1 min-h-0 overflow-hidden flex flex-col gap-4">
          {/* Meta row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground/90">{t("Title")}</label>
              <Input
                value={title}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
                placeholder={t("e.g. Mowazaf General Terms of Service")}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground/90">{t("Effective from (optional)")}</label>
              <DateField
                value={effectiveFrom}
                onChange={(v) => setEffectiveFrom(v)}
              />
            </div>
          </div>

          {/* Editor + preview */}
          <div
            className={
              view === "split"
                ? "grid grid-cols-1 lg:grid-cols-2 gap-3 flex-1 min-h-0"
                : "flex-1 min-h-0"
            }
          >
            {(view === "edit" || view === "split") && (
              <div className="flex flex-col min-h-0">
                <label className="text-xs font-medium text-muted-foreground mb-1.5">
                  {t("Markdown")}
                </label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  spellCheck={false}
                  className="flex-1 min-h-[360px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono leading-relaxed placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors"
                  placeholder={t("# Heading\n\nMarkdown supported: **bold**, *italic*, [links](https://…), lists, > blockquotes.", { nsSeparator: false })}
                />
              </div>
            )}
            {(view === "preview" || view === "split") && (
              <div className="flex flex-col min-h-0">
                <label className="text-xs font-medium text-muted-foreground mb-1.5">
                  {t("Preview")}
                </label>
                <div className="flex-1 min-h-[360px] overflow-y-auto rounded-md border border-border bg-muted/20 px-4 py-3">
                  <SafeMarkdown content={content || `*${t("Nothing to preview yet.")}*`} />
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 p-3 text-sm text-red-800 dark:text-red-300">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </DialogBody>

        <DialogFooter className="justify-between">
          <div className="text-xs text-muted-foreground">
            {t("Saving keeps this as a draft. Publish from the table to make it live.")}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              {t("Cancel")}
            </Button>
            <Button onClick={submit} loading={submitting}>
              {t("Save draft")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface ViewBtnProps {
  active: boolean;
  onClick: () => void;
  label: string;
  icon: React.ReactNode;
}
const ViewBtn: React.FC<ViewBtnProps> = ({ active, onClick, label, icon }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={
      "inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium transition-colors " +
      (active
        ? "bg-card text-foreground shadow-sm"
        : "text-muted-foreground hover:text-foreground")
    }
  >
    {icon}
    {label}
  </button>
);

export default TermsDraftDialog;
